/**
 * Skip Links Component
 * Provides keyboard shortcuts for screen reader users to navigate to important sections
 */

import React, { useEffect } from 'react';
import { useSkipLinks } from '../../hooks/useAccessibility';

interface SkipLink {
  id: string;
  text: string;
  target: string;
}

interface SkipLinksProps {
  links?: SkipLink[];
  className?: string;
}

const defaultSkipLinks: SkipLink[] = [
  { id: 'skip-main', text: 'Skip to main content', target: 'main-content' },
  { id: 'skip-nav', text: 'Skip to navigation', target: 'main-navigation' },
  { id: 'skip-search', text: 'Skip to search', target: 'search-section' },
  { id: 'skip-footer', text: 'Skip to footer', target: 'main-footer' }
];

const SkipLinks: React.FC<SkipLinksProps> = ({
  links = defaultSkipLinks,
  className = ''
}) => {
  const { registerSkipTarget, unregisterSkipTarget, focusSkipTarget } = useSkipLinks();

  // Register skip targets when component mounts
  useEffect(() => {
    links.forEach(link => {
      const targetElement = document.getElementById(link.target);
      if (targetElement) {
        registerSkipTarget(link.id, targetElement);
      }
    });

    // Cleanup on unmount
    return () => {
      links.forEach(link => {
        unregisterSkipTarget(link.id);
      });
    };
  }, [links, registerSkipTarget, unregisterSkipTarget]);

  const handleSkipClick = (event: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    event.preventDefault();
    focusSkipTarget(targetId);
  };

  return (
    <div className={`skip-links-container ${className}`}>
      {links.map(link => (
        <a
          key={link.id}
          href={`#${link.target}`}
          id={link.id}
          className="skip-link"
          onClick={(e) => handleSkipClick(e, link.id)}
          aria-label={`Skip to ${link.text.toLowerCase()}`}
        >
          {link.text}
        </a>
      ))}
    </div>
  );
};

export default SkipLinks;
